// "How I knew" — the retrieval trace of one answer, folded under the message.
// Reads the trace ask() returns (backend/shared/retrievalTrace.js): which
// strata answered, each hit with its legs and score, how much context the
// model saw against its budgets, and who the principal was. Identical in the
// plane and the kit; `hrefFor(hit)` is the caller's citation link.
//
// When the trace carries a think stream (`trace.think`, retrievalTrace.js)
// a **Watch** toggle replays the search in a small BrainThink player. The
// caller supplies the map: `graph` (already loaded) or `loadGraph()` (async,
// fetched on first watch); without either the player shows the stages and
// the sources rail alone. `thinkHref` links to the full Think view.
import React, { useEffect, useState } from 'react';
import BrainThink from './BrainThink.js';
import './HowIKnew.css';

const STRATUM_LABEL = { 'app-brain': "this app's brain", brain: 'project brain', docs: 'product docs', design: 'app design', 'kit-docs': 'kit docs', org: 'documents' };
const LEG_LABEL = { vector: 'meaning', keyword: 'words', graph: 'related' };

export default function HowIKnew({ trace, hrefFor, LinkComponent = 'a', graph = null, loadGraph = null, thinkHref = null }) {
  const [open, setOpen] = useState(false);
  const [watch, setWatch] = useState(false);
  const [g, setG] = useState(graph);
  useEffect(() => { if (graph) setG(graph); }, [graph]);
  useEffect(() => {
    if (!watch || g || !loadGraph) return undefined;
    let live = true;
    Promise.resolve(loadGraph()).then((x) => { if (live && x) setG(x); }).catch(() => {});
    return () => { live = false; };
  }, [watch, g, loadGraph]);
  if (!trace || (!trace.hits && !trace.think)) return null;
  const hits = trace.hits || [];
  const think = trace.think?.events?.length ? trace.think : null;
  const strata = Object.entries(trace.strata || {}).sort((a, b) => b[1] - a[1]);
  const ctxChars = trace.context?.chars || 0;
  const ctxBudget = (trace.budgets?.topK || 8) * (trace.budgets?.parentContext ? 3600 : trace.budgets?.chunkSize || 1400);
  const legsTotal = Object.values(trace.legs || {}).reduce((a, b) => a + b, 0) || 1;
  const Link = LinkComponent;
  const thinkLink = (docId) => (hrefFor ? hrefFor({ docId }) : null);
  return (
    <div className={`hik ${open ? 'is-open' : ''}`} data-nova-ignore="true">
      <button type="button" className="hik-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? '▾' : '▸'} How I knew
        <span className="hik-sum">
          {hits.length ? `${hits.length} source${hits.length === 1 ? '' : 's'}` : think ? `${think.total || think.events.length} steps` : 'no sources'}
          {strata.length ? ` · ${strata.map(([s]) => STRATUM_LABEL[s] || s).join(', ')}` : ''}
          {trace.ms ? ` · ${(trace.ms / 1000).toFixed(1)}s` : ''}
        </span>
      </button>
      {think ? (
        <button type="button" className={`hik-toggle hik-watch ${watch ? 'is-on' : ''}`} onClick={() => setWatch((v) => !v)} aria-pressed={watch} title="Replay how this answer was found">
          {watch ? '■' : '▶'} Watch
        </button>
      ) : null}
      {watch && think ? (
        <div className="hik-player">
          <BrainThink graph={g} events={think.events} height={300} hrefFor={thinkLink} LinkComponent={LinkComponent} />
          {thinkHref ? <Link to={thinkHref} href={thinkHref} className="hik-think-link">Open on the Brain →</Link> : null}
        </div>
      ) : null}
      {open ? (
        <div className="hik-body">
          <div className="hik-row">
            <span className="hik-k">Looked in</span>
            <span className="hik-chips">{strata.length ? strata.map(([s, n]) => <span key={s} className={`hik-chip is-${s}`}>{STRATUM_LABEL[s] || s} <b>{n}</b></span>) : <em>nothing matched</em>}</span>
          </div>
          <div className="hik-row">
            <span className="hik-k">Found by</span>
            <span className="hik-legs">
              {Object.entries(trace.legs || {}).map(([leg, n]) => (
                <span key={leg} className={`hik-leg is-${leg}`} title={`${n} hits through the ${leg} leg`}>
                  <i style={{ width: `${Math.round((n / legsTotal) * 100)}%` }} />{LEG_LABEL[leg] || leg} {n}
                </span>
              ))}
            </span>
          </div>
          {trace.variants?.length > 1 ? (
            <div className="hik-row"><span className="hik-k">Also asked</span><span className="hik-v">{trace.variants.slice(1).map((v) => <em key={v}>“{v}”</em>)}</span></div>
          ) : null}
          <ol className="hik-hits">
            {hits.map((h, i) => {
              const href = hrefFor ? hrefFor(h) : null;
              return (
                <li key={`${h.docId}-${i}`}>
                  {href ? <Link to={href} href={href} className="hik-title">{h.title || h.docId}</Link> : <span className="hik-title">{h.title || h.docId}</span>}
                  <span className={`hik-chip is-${h.stratum}`}>{STRATUM_LABEL[h.stratum] || h.stratum}</span>
                  {(h.legs || []).map((l) => <span key={l} className={`hik-leg-badge is-${l}`}>{LEG_LABEL[l] || l}</span>)}
                  {h.parent ? <span className="hik-leg-badge is-parent" title="answered from the whole section, not just the matching chunk">section</span> : null}
                  <span className="hik-score" title="fused rank score">{h.rerank != null ? `rerank ${Number(h.rerank).toFixed(2)}` : h.score != null ? Number(h.score).toFixed(3) : ''}</span>
                </li>
              );
            })}
          </ol>
          <div className="hik-row">
            <span className="hik-k">Context</span>
            <span className="hik-bar" title={`${ctxChars.toLocaleString()} characters handed to the answer model (≈ ${Math.round(ctxChars / 4).toLocaleString()} tokens)`}>
              <i style={{ width: `${Math.min(100, Math.round((ctxChars / Math.max(ctxBudget, 1)) * 100))}%` }} />
              <span>{ctxChars.toLocaleString()} chars{trace.context?.parentBlocks ? ` · ${trace.context.parentBlocks} section${trace.context.parentBlocks === 1 ? '' : 's'}` : ''}{trace.budgets?.answerMaxTokens ? ` → answer ≤ ${trace.budgets.answerMaxTokens} tokens` : ''}</span>
            </span>
          </div>
          <div className="hik-row hik-fine">
            <span className="hik-k">Budgets</span>
            <span className="hik-v">top {trace.budgets?.topK ?? '—'} of {trace.budgets?.vectorK ?? '—'} per leg{trace.budgets?.rewriteVariants ? ` · ${trace.budgets.rewriteVariants} query variants` : ''}{trace.budgets?.rerankK ? ` · rerank ${trace.budgets.rerankK}` : ''}{trace.quality?.faithfulness != null ? ` · faithfulness ${Math.round(trace.quality.faithfulness * 100)}%` : ''}</span>
          </div>
          <div className="hik-row hik-fine">
            <span className="hik-k">Acting as</span>
            <span className="hik-v">{trace.principal?.publicOnly ? 'a visitor (public knowledge only)' : trace.principal?.internal ? 'a member (internal knowledge included)' : 'a signed-in user'}{trace.principal?.orgScoped ? ' · scoped to this organization' : ''}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
